import {getSafely} from 'shared/web';

import {PriceBand} from './getPriceBands';

export interface BandAxisTick {
  /** Unclamped X position in SVG units. */
  x: number;
  price: number;
  /** Cumulative share (0–100) of vehicles priced below this boundary. */
  percent: number;
}

interface BuildBandAxisTicksInput {
  /** Bands shown by the chart, ascending. */
  bands: PriceBand[];
  /** Vehicle prices sorted ascending. */
  sortedPrices: number[];
  /** Pixel (SVG-unit) width of one band column. */
  slotWidth: number;
  /** Total chart width in SVG units. */
  width: number;
  /** Minimum room a boundary label needs; labels thin out below it. */
  minLabelWidth: number;
}

const PERCENT = 100;

/** Counts how many of the ascending `prices` lie below each ascending boundary. */
export function countBelowEach(prices: number[], boundaries: number[]): number[] {
  let walkIndex = 0;
  return boundaries.map((boundary) => {
    while (walkIndex < prices.length && (getSafely(prices, walkIndex) ?? Infinity) < boundary) {
      walkIndex += 1;
    }
    return walkIndex;
  });
}

/**
 * Bottom-axis ticks for the band-axis distribution: every band boundary gets a
 * price label while there is room for it; labels thin to every nth boundary once
 * the columns get too narrow, and a boundary too close to the closing tick is
 * dropped so the two labels never overlap. Each tick carries the share of vehicles
 * priced below it; the closing tick is 100 % by construction — it sits at the end
 * of the last shown band (or at the data maximum when the top band is open-ended).
 */
export function buildBandAxisTicks(input: BuildBandAxisTicksInput): BandAxisTick[] {
  const {bands, sortedPrices, slotWidth, width, minLabelWidth} = input;
  if (bands.length === 0) {
    return [];
  }

  const labelStep = Math.max(1, Math.ceil(minLabelWidth / slotWidth));
  const lastBandTo = bands[bands.length - 1]?.to;
  const maxPrice = sortedPrices[sortedPrices.length - 1] ?? 0;
  const endTick: BandAxisTick = {x: width, price: lastBandTo ?? maxPrice, percent: PERCENT};

  const boundaryTicks = bands
    .map((band, bandIndex) => ({x: bandIndex * slotWidth, price: band.from}))
    .filter((tick, bandIndex) => bandIndex % labelStep === 0 && width - tick.x >= minLabelWidth);
  const cheaperCounts = countBelowEach(
    sortedPrices,
    boundaryTicks.map((tick) => tick.price)
  );
  const total = Math.max(1, sortedPrices.length);

  return [
    ...boundaryTicks.map((tick, tickIndex) => ({
      ...tick,
      percent: Math.round(((getSafely(cheaperCounts, tickIndex) ?? 0) / total) * PERCENT),
    })),
    endTick,
  ];
}
